/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldExclamationIcon, XMarkIcon, ArrowRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

interface KYCRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  kycStatus?: string;
  message?: string;
}

const KYCRequiredModal: React.FC<KYCRequiredModalProps> = ({
  isOpen,
  onClose,
  kycStatus = 'not_submitted',
  message,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleCompleteKYC = () => {
    onClose();
    navigate('/kyc');
  };

  const getStatusInfo = () => {
    switch (kycStatus) {
      case 'pending':
        return {
          title: 'KYC Verification In Review',
          description: 'Your KYC verification is being reviewed. This usually takes a few minutes.',
          showEmailSupport: true,
          buttonText: 'View Status',
        };
      case 'in_progress':
        return {
          title: 'KYC Verification In Progress',
          description: 'Please complete your KYC verification process to continue.',
          showEmailSupport: false,
          buttonText: 'Continue Verification',
        };
      case 'rejected':
        return {
          title: 'KYC Verification Declined',
          description: 'Your verification was declined. Please try again with valid documents.',
          showEmailSupport: true,
          buttonText: 'Retry Verification',
        };
      case 'expired':
        return {
          title: 'KYC Verification Expired',
          description: 'Your verification session has expired. Please start a new verification.',
          showEmailSupport: false,
          buttonText: 'Start Verification',
        };
      default:
        return {
          title: 'KYC Verification Required',
          description: 'Complete your identity verification to provision resources on Podstack.',
          showEmailSupport: false,
          buttonText: 'Complete KYC',
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-xl">
              <ShieldExclamationIcon className="w-8 h-8 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {statusInfo.title}
              </h2>
              <p className="text-sm text-gray-400">Identity verification</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-4 space-y-4">
          <p className="text-gray-300 leading-relaxed">
            {message || statusInfo.description}
          </p>

          {/* Info Box */}
          <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700/50">
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="font-medium text-orange-400">Why KYC?</span>
              <br />
              KYC verification is required to maintain a secure platform and comply with regulations.
              You can explore the platform, but cannot provision any resources until verification is complete.
            </p>
          </div>

          {/* Email Support Section for Pending Status */}
          {statusInfo.showEmailSupport && (
            <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <div className="flex items-start gap-3">
                <EnvelopeIcon className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-300">Need Help?</p>
                  <p className="text-sm text-amber-200/80 mt-1">
                    If your verification is taking longer than expected, please contact our support team at{' '}
                    <a
                      href="mailto:support@podstack.ai"
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      support@podstack.ai
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-2 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleCompleteKYC}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
          >
            {statusInfo.buttonText}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default KYCRequiredModal;
